import i18n from 'i18next';
import type { SupportedLanguage } from './config';
import type { TranslationJsonValue } from './jsonTypes';

/**
 * Performance monitoring and optimization for i18n operations
 */
class I18nPerformance {
  private cache = new Map<string, TranslationJsonValue>();
  private lastSwitchTime = 0;
  private preloadPromises = new Map<string, Promise<boolean>>();

  /**
   * Get the translation cache
   */
  getCache(): Map<string, TranslationJsonValue> {
    return this.cache;
  }

  /**
   * Get cache size
   */
  getCacheSize(): number {
    return this.cache.size;
  }

  /**
   * Get last language switch time
   */
  getLastSwitchTime(): number {
    return this.lastSwitchTime;
  }

  /**
   * Set last language switch time
   */
  setLastSwitchTime(time: number): void {
    this.lastSwitchTime = time;
  }

  /**
   * Clear the translation cache
   */
  clearCache(): void {
    this.cache.clear();
    this.preloadPromises.clear();
  }

  /**
   * Preload a language for faster switching
   */
  async preloadLanguage(language: SupportedLanguage): Promise<boolean> {
    // Check if already preloading
    if (this.preloadPromises.has(language)) {
      return this.preloadPromises.get(language)!;
    }

    // Check if already cached
    const isCached = Array.from(this.cache.keys()).some(key => key.startsWith(language));
    if (isCached) {
      return true;
    }

    // Create preload promise
    const preloadPromise = this.performPreload(language);
    this.preloadPromises.set(language, preloadPromise);

    try {
      const result = await preloadPromise;
      return result;
    } finally {
      this.preloadPromises.delete(language);
    }
  }

  /**
   * Perform the actual preloading
   */
  private async performPreload(language: SupportedLanguage): Promise<boolean> {
    try {
      // Load all namespaces for the language
      const namespaces = ['common', 'auth', 'detection', 'statistics', 'profile', 'navigation', 'errors', 'history', 'home', 'device'];
      
      const loadPromises = namespaces.map(async (ns) => {
        try {
          const response = await fetch(`/locales/${language}/${ns}.json`);
          if (response.ok) {
            const data = await response.json();
            const cacheKey = `${language}:${ns}`;
            this.cache.set(cacheKey, data);
            return true;
          }
          return false;
        } catch (error) {
          console.warn(`Failed to preload ${language}:${ns}`, error);
          return false;
        }
      });

      const results = await Promise.all(loadPromises);
      const successCount = results.filter(Boolean).length;
      
      // Consider successful if at least half of the namespaces loaded
      const success = successCount >= Math.ceil(namespaces.length / 2);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`Preloaded ${language}: ${successCount}/${namespaces.length} namespaces`);
      }

      return success;
    } catch (error) {
      console.error(`Failed to preload language ${language}:`, error);
      return false;
    }
  }

  /**
   * Check if a specific namespace is cached for a language
   */
  isNamespaceCached(language: SupportedLanguage, namespace: string): boolean {
    return this.cache.has(`${language}:${namespace}`);
  }

  /**
   * Get cached translation data
   */
  getCachedData(language: SupportedLanguage, namespace: string): TranslationJsonValue | undefined {
    return this.cache.get(`${language}:${namespace}`);
  }

  /**
   * Set cached translation data
   */
  setCachedData(
    language: SupportedLanguage,
    namespace: string,
    data: TranslationJsonValue,
  ): void {
    this.cache.set(`${language}:${namespace}`, data);
  }

  /**
   * Get performance metrics
   */
  getMetrics() {
    return {
      cacheSize: this.getCacheSize(),
      lastSwitchTime: this.getLastSwitchTime(),
      cachedLanguages: this.getCachedLanguages(),
      preloadingLanguages: Array.from(this.preloadPromises.keys())
    };
  }

  /**
   * Get list of cached languages
   */
  private getCachedLanguages(): string[] {
    const languages = new Set<string>();
    for (const key of this.cache.keys()) {
      const [lang] = key.split(':');
      languages.add(lang);
    }
    return Array.from(languages);
  }
}

// Create singleton instance
export const i18nPerformance = new I18nPerformance();

// Hook into i18next events to update cache
if (typeof window !== 'undefined') {
  // Listen for resource loading events
  i18n.on('loaded', (loaded) => {
    Object.entries(loaded).forEach(([lang, namespaces]) => {
      if (typeof namespaces === 'object' && namespaces !== null) {
        Object.entries(namespaces).forEach(([ns, data]) => {
          i18nPerformance.setCachedData(lang as SupportedLanguage, ns, data);
        });
      }
    });
  });

  // Listen for language change events
  i18n.on('languageChanged', () => {
    i18nPerformance.setLastSwitchTime(performance.now());
  });
}

export default i18nPerformance;
